import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Crown, Clock, Target, Star } from 'lucide-react';
import Button from '../../../components/ui/Button';

const TeamChallenges = () => {
  const [activeTeams, setActiveTeams] = useState([]);

  const teamChallenges = [
    {
      id: 'team-1',
      title: 'E-commerce Platform Integration Test',
      description: 'Collaborate with a team of 4 to test a complete e-commerce platform including frontend, backend APIs, and payment integration.',
      teamSize: 4,
      currentMembers: 3,
      difficulty: 'Advanced',
      timeLimit: 120,
      points: 800,
      requiredRoles: ['UI Tester', 'API Tester', 'Performance Tester', 'Security Tester'],
      startDate: '2025-01-10',
      skills: ['Selenium', 'Postman', 'JMeter', 'OWASP'],
      teamLead: 'TestMaster_Pro',
      industry: 'E-commerce'
    },
    {
      id: 'team-2',
      title: 'Banking Application Security Audit',
      description: 'Multi-disciplinary team challenge to perform comprehensive security testing on a banking application.',
      teamSize: 6,
      currentMembers: 4,
      difficulty: 'Expert',
      timeLimit: 180,
      points: 1200,
      requiredRoles: ['Security Analyst', 'Penetration Tester', 'Code Reviewer', 'Compliance Tester'],
      startDate: '2025-01-15',
      skills: ['Burp Suite', 'OWASP ZAP', 'Static Analysis', 'Compliance Testing'],
      teamLead: 'SecuritySage',
      industry: 'FinTech'
    },
    {
      id: 'team-3',
      title: 'Microservices Performance Challenge',
      description: 'Test performance and reliability of a complex microservices architecture under various load conditions.',
      teamSize: 5,
      currentMembers: 2,
      difficulty: 'Expert',
      timeLimit: 150,
      points: 1000,
      requiredRoles: ['Performance Engineer', 'DevOps Tester', 'Monitoring Specialist', 'Load Tester'],
      startDate: '2025-01-20',
      skills: ['K6', 'Grafana', 'Kubernetes', 'Docker'],
      teamLead: 'PerformanceGuru',
      industry: 'Enterprise'
    }
  ];

  const handleJoinTeam = (challengeId) => {
    console.log('Joining team challenge:', challengeId);
    // In a real app, this would handle team joining logic
  };

  const handleCreateTeam = () => {
    console.log('Creating new team');
    // In a real app, this would open team creation modal
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'intermediate': return 'text-blue-600 bg-blue-100';
      case 'advanced': return 'text-purple-600 bg-purple-100';
      case 'expert': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Team Challenges</h2>
          <p className="text-muted-foreground">
            Collaborate with other testers to solve complex, real-world testing scenarios
          </p>
        </div>
        <Button onClick={handleCreateTeam} className="flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Create Team</span>
        </Button>
      </div>
      {/* Team Challenge Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg p-4 text-center">
          <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-blue-700">{teamChallenges?.length}</div>
          <div className="text-sm text-blue-600">Active Teams</div>
        </div>
        <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-lg p-4 text-center">
          <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-700">12</div>
          <div className="text-sm text-green-600">Open Positions</div>
        </div>
        <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-4 text-center">
          <Crown className="w-8 h-8 text-purple-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-purple-700">8</div>
          <div className="text-sm text-purple-600">Team Leads</div>
        </div>
        <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-lg p-4 text-center">
          <Star className="w-8 h-8 text-orange-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-orange-700">2400</div>
          <div className="text-sm text-orange-600">Avg Team Points</div>
        </div>
      </div>
      {/* Team Challenges List */}
      <div className="space-y-6">
        {teamChallenges?.map((challenge, index) => (
          <motion.div
            key={challenge?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-card border border-border rounded-xl p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-lg font-semibold">{challenge?.title}</h3>
                  <span className={`text-xs px-2 py-1 rounded font-medium ${getDifficultyColor(challenge?.difficulty)}`}>
                    {challenge?.difficulty}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm mb-4">
                  {challenge?.description}
                </p>
              </div>
              <div className="text-right ml-4">
                <div className="text-2xl font-bold text-yellow-600">{challenge?.points}</div>
                <div className="text-xs text-muted-foreground">points</div>
              </div>
            </div>

            {/* Team Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Team Members</span>
                <span className="text-sm text-muted-foreground">
                  {challenge?.currentMembers}/{challenge?.teamSize}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(challenge?.currentMembers / challenge?.teamSize) * 100}%` }}
                />
              </div>
            </div>

            {/* Challenge Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h4 className="font-medium text-sm mb-2">Required Roles</h4>
                <div className="flex flex-wrap gap-1">
                  {challenge?.requiredRoles?.map((role, idx) => (
                    <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {role}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">Skills Needed</h4>
                <div className="flex flex-wrap gap-1">
                  {challenge?.skills?.map((skill, idx) => (
                    <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Team Info and Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span className="font-medium">{challenge?.teamLead}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{challenge?.timeLimit} min</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-muted-foreground">Starts:</span>
                  <span>{new Date(challenge?.startDate)?.toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {challenge?.currentMembers < challenge?.teamSize ? (
                  <Button 
                    size="sm"
                    onClick={() => handleJoinTeam(challenge?.id)}
                  >
                    Join Team
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" disabled>
                    Team Full
                  </Button>
                )}
                <Button size="sm" variant="ghost">
                  View Details
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Empty State */}
      {teamChallenges?.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No team challenges available</h3>
          <p className="text-muted-foreground mb-4">
            Create a new team challenge or check back later for new opportunities
          </p>
          <Button onClick={handleCreateTeam}>
            Create First Team Challenge
          </Button>
        </div>
      )}
      {/* How Team Challenges Work */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">How Team Challenges Work</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-blue-600 font-bold">1</span>
            </div>
            <h4 className="font-medium mb-2">Join or Create Team</h4>
            <p className="text-sm text-muted-foreground">
              Find teammates with complementary skills or start your own team
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-purple-600 font-bold">2</span>
            </div>
            <h4 className="font-medium mb-2">Collaborate & Test</h4>
            <p className="text-sm text-muted-foreground">
              Work together using shared tools and communicate in real-time
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-green-600 font-bold">3</span>
            </div>
            <h4 className="font-medium mb-2">Share Rewards</h4>
            <p className="text-sm text-muted-foreground">
              Earn points, badges, and achievements together as a team
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TeamChallenges;